// FullscreenMessage.jsx
import * as React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Stack,
  CircularProgress
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { FormattedMessage } from 'react-intl';

const iconByVariant = {
  info:    <InfoOutlinedIcon fontSize="inherit" />,
  success: <CheckCircleOutlineIcon fontSize="inherit" />,
  warning: <WarningAmberOutlinedIcon fontSize="inherit" />,
  error:   <ErrorOutlineIcon fontSize="inherit" />
};

export default function FullscreenMessage({
  message,            // i18n id (required)
  title,              // optional i18n id for headline
  description,        // optional i18n id for supporting text
  variant = 'info',   // 'info' | 'success' | 'warning' | 'error'
  loading = false,    // show spinner
  primaryAction,      // { labelId, onClick }
  secondaryAction     // { labelId, onClick }
}) {
  return (
    <Box
      sx={{
        minHeight: '100svh', // mobile safe viewport height
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'linear-gradient(180deg, rgba(0,0,0,0.03) 0%, rgba(0,0,0,0.06) 100%)'
      }}
    >
      <Card
        elevation={3}
        sx={{
          width: '100%',
          maxWidth: 520,
          borderRadius: 3
        }}
      >
        <CardContent>
          <Stack spacing={2} alignItems="center" textAlign="center">
            {/* Icon + optional loader */}
            <Box sx={{ fontSize: 48, lineHeight: 1 }}>
              {iconByVariant[variant] || iconByVariant.info}
            </Box>

            {/* Title (optional) */}
            {title && (
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                <FormattedMessage id={title} defaultMessage={`[${title}]`} />
              </Typography>
            )}

            {/* Main message (required) */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: title ? 500 : 700,
                wordBreak: 'break-word'
              }}
            >
              <FormattedMessage id={message} defaultMessage={`[${message}]`} />
            </Typography>

            {/* Description (optional, smaller) */}
            {description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ maxWidth: 440 }}
            >
                <FormattedMessage id={description} defaultMessage={`[${description}]`} />
              </Typography>
            )}

            {/* Loading indicator (optional) */}
            {loading && (
              <Box sx={{ mt: 1 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Stack>
        </CardContent>

        {(primaryAction || secondaryAction) && (
          <CardActions
            sx={{
              px: 2,
              pb: 2,
              pt: 0,
              display: 'flex',
              gap: 1.5,
              justifyContent: 'center'
            }}
          >
            {secondaryAction && (
              <Button
                variant="outlined"
                size="large"
                onClick={secondaryAction.onClick}
              >
                <FormattedMessage
                  id={secondaryAction.labelId}
                  defaultMessage={`[${secondaryAction.labelId}]`}
                />
              </Button>
            )}
            {primaryAction && (
              <Button
                variant="contained"
                size="large"
                onClick={primaryAction.onClick}
              >
                <FormattedMessage
                  id={primaryAction.labelId}
                  defaultMessage={`[${primaryAction.labelId}]`}
                />
              </Button>
            )}
          </CardActions>
        )}
      </Card>
    </Box>
  );
}
